import { useMemo, useState } from "react";
import { PRODUCT_COL_WIDTH } from "@/lib/gridLayout";
import { Badge } from "@/components/ui/Badge";
import StockMatrixHeader, {
  TOTAL_COL_WIDTH,
  COL_WIDTH,
  cellBorder,
  type WarehouseColumn,
} from "./StockMatrixHeader";
import StockHealthCells, { type HealthColumn } from "./StockHealthCells";
import GroupHeader from "./GroupHeader";
import type { Group } from "../lib/groupProducts";
import { summarizeStock, type MatrixSort, type StockHealthFields } from "../lib/stockHealth";
import { buildMatrixLayout, columnStock } from "../lib/matrixColumns";

export type { WarehouseColumn };

// Tall enough for the Status cell's badge plus its "(Default lead time)" line.
const ROW_HEIGHT = 44;

export interface StockMatrixRow extends StockHealthFields {
  external_id: string;
  name: string;
  full_name: string | null;
  product_line_external_id: string | null;
  product_line_name: string | null;
  product_line_full_name: string | null;
  product_line_short_name: string | null;
  product_node_id: string | null;
  product_node_path: string | null;
  product_node_lineage_ids: string[];
  product_node_order: number | null;
  total: number;
  // Sparse: warehouses with zero stock for the product are absent.
  stocks: Record<string, number>;
}

export interface StockMatrixGridProps {
  warehouses: WarehouseColumn[];
  products: StockMatrixRow[];
  healthColumns: HealthColumn[];
  currency: string;
  // Whether this user may see cost-based figures (the group headers' value).
  valueVisible: boolean;
  // When set, rows render under group headers that open and close, instead of flat.
  // `products` is still used for the empty state.
  groups?: Group<StockMatrixRow>[];
  onSelectProduct: (p: StockMatrixRow) => void;
  sidebarOpen: boolean;
  // Rows arrive already sorted; the header only shows and changes the sort.
  sort: MatrixSort;
  onSortChange: (sort: MatrixSort) => void;
  // Distinguishes "nothing in stock" from "nothing matches the search".
  filtered: boolean;
  // Rows of more than one kind are on screen, so gift and not moving rows say
  // which they are.
  showKind?: boolean;
  // Closed product groups, when the page controls them (Expand all / Collapse
  // all). Without these the grid keeps its own.
  closedGroupKeys?: Set<string>;
  onToggleGroup?: (key: string) => void;
}

function Qty({ value, strong = false }: { value: number | undefined; strong?: boolean }) {
  if (!value) {
    return <span style={{ color: "var(--chart-muted)" }}>–</span>;
  }
  return (
    <span
      className="tabular-nums"
      style={{
        color: value < 0 ? "var(--destructive)" : "var(--foreground)",
        fontWeight: strong ? 600 : 400,
      }}
    >
      {value.toLocaleString()}
    </span>
  );
}

function toggled(set: Set<string>, key: string): Set<string> {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

// The header rows and product rows, without a scroll box: the all-markets
// page stacks one per market inside a single scroll box. The header sticks to
// the top of the scroll box while this grid's rows pass under it.
export default function StockMatrixGrid({
  warehouses,
  products,
  healthColumns,
  currency,
  valueVisible,
  groups,
  onSelectProduct,
  sidebarOpen,
  sort,
  onSortChange,
  filtered,
  showKind = false,
  closedGroupKeys: controlledClosedGroupKeys,
  onToggleGroup,
}: StockMatrixGridProps) {
  // Channels with several warehouses start folded into one total column on
  // every page load; the viewer opens them from the header band.
  const [openChannelIds, setOpenChannelIds] = useState<Set<string>>(() => new Set());
  const layout = useMemo(
    () => buildMatrixLayout(warehouses, openChannelIds),
    [warehouses, openChannelIds],
  );
  const toggleChannel = (channelId: string) =>
    setOpenChannelIds((prev) => toggled(prev, channelId));

  // Every product group starts open; the viewer closes the ones they skip.
  const [ownClosedGroupKeys, setOwnClosedGroupKeys] = useState<Set<string>>(() => new Set());
  const closedGroupKeys = controlledClosedGroupKeys ?? ownClosedGroupKeys;
  const toggleGroup = (key: string) =>
    onToggleGroup ? onToggleGroup(key) : setOwnClosedGroupKeys((prev) => toggled(prev, key));
  const groupSummaries = useMemo(() => {
    const warehouseIds = warehouses.map((w) => w.external_id);
    return new Map((groups ?? []).map((g) => [g.key, summarizeStock(g.rows, warehouseIds)]));
  }, [groups, warehouses]);

  const totalWidth =
    PRODUCT_COL_WIDTH +
    TOTAL_COL_WIDTH +
    healthColumns.reduce((sum, c) => sum + c.width, 0) +
    layout.columns.length * COL_WIDTH;

  const renderRow = (p: StockMatrixRow) => (
    <div
      key={p.external_id}
      style={{
        display: "flex",
        height: ROW_HEIGHT,
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div
        style={{
          width: PRODUCT_COL_WIDTH,
          flexShrink: 0,
          position: "sticky",
          left: 0,
          zIndex: sidebarOpen ? 45 : 2,
          background: "var(--background)",
          display: "flex",
          alignItems: "center",
          padding: "0 12px",
          fontSize: 12,
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
        title={`${p.external_id} ${p.name}`}
      >
        <span
          onClick={() => onSelectProduct(p)}
          className="cursor-pointer"
          style={{ color: "var(--muted-foreground)", marginRight: 4 }}
        >
          {p.external_id}
        </span>
        <span
          onClick={() => onSelectProduct(p)}
          className="cursor-pointer hover:underline"
          style={{ overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {p.name}
        </span>
        {showKind && p.kind !== "for_sale" && (
          <Badge variant="outline" className="ml-1.5 shrink-0 px-1.5 py-0 font-medium">
            {p.kind === "gift" ? "Gift" : "Not moving"}
          </Badge>
        )}
        {p.is_new && (
          <Badge variant="info" className="ml-1.5 shrink-0 px-1.5 py-0">
            New
          </Badge>
        )}
      </div>
      <div
        style={{
          width: TOTAL_COL_WIDTH,
          flexShrink: 0,
          position: "sticky",
          left: PRODUCT_COL_WIDTH,
          zIndex: sidebarOpen ? 45 : 2,
          background: "var(--background)",
          borderRight: "2px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "0 8px",
          fontSize: 12,
        }}
      >
        <Qty value={p.total} strong />
      </div>
      <StockHealthCells
        columns={healthColumns}
        row={p}
        currency={currency}
        onClick={() => onSelectProduct(p)}
      />
      {layout.columns.map((c) => (
        <div
          key={c.key}
          onClick={() => onSelectProduct(p)}
          className="cursor-pointer"
          style={{
            flex: `1 0 ${COL_WIDTH}px`,
            borderRight: cellBorder(layout.bandEndKeys.has(c.key)),
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            padding: "0 8px",
            fontSize: 12,
          }}
        >
          <Qty value={columnStock(p.stocks, c)} />
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ minWidth: totalWidth }}>
      <StockMatrixHeader
        layout={layout}
        onToggleChannel={toggleChannel}
        healthColumns={healthColumns}
        sidebarOpen={sidebarOpen}
        sort={sort}
        onSortChange={onSortChange}
      />

      {groups
        ? groups.map((g) => (
            <div key={g.key}>
              <GroupHeader
                label={g.label}
                productCount={g.rows.length}
                summary={groupSummaries.get(g.key)!}
                currency={currency}
                valueVisible={valueVisible}
                open={!closedGroupKeys.has(g.key)}
                onToggle={() => toggleGroup(g.key)}
              />
              {!closedGroupKeys.has(g.key) && g.rows.map(renderRow)}
            </div>
          ))
        : products.map(renderRow)}

      {products.length === 0 && (
        <div className="px-6 py-10 text-sm text-muted-foreground">
          {filtered ? "No products match your search or filters." : "No products in stock."}
        </div>
      )}
    </div>
  );
}
